import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Building2, Shield, RefreshCw, LogOut } from 'lucide-react';

interface TenantOption {
  tenantId: string;
  tenantName: string;
  isActive: boolean;
}

interface AdminLoginResponse {
  isAdmin: boolean;
  adminId: string;
  adminName: string;
  availableTenants: TenantOption[];
}

export default function TenantSelection() {
  const navigate = useNavigate();
  const location = useLocation();
  const adminData = location.state as AdminLoginResponse | null;

  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!adminData || !adminData.isAdmin) {
      navigate('/login');
      return;
    }
    setTenants(adminData.availableTenants || []);
  }, [adminData, navigate]);

  const handleTenantSelect = async (tenantId: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/Account/admin/impersonate-tenant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tenantId })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Impersonating tenant:', data);
        // Redirect to main dashboard
        window.location.href = '/';
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to select tenant');
      }
    } catch (err) {
      console.error('Error selecting tenant:', err);
      setError('Network error while selecting tenant');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminPanel = () => {
    navigate('/admin/tenants');
  };

  const handleLogout = async () => {
    try {
      await fetch('/Account/logout', {
        method: 'POST',
        credentials: 'include',
      });
      // Redirect to login page
      window.location.href = '/';
    } catch (err) {
      console.error('Error logging out:', err);
      alert('Failed to logout');
    }
  };

  if (!adminData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-3xl flex items-center gap-2">
                <Shield className="h-8 w-8 text-blue-600" />
                Admin Portal
              </CardTitle>
              <CardDescription className="mt-2">
                Welcome, <strong>{adminData.adminName}</strong>. Select a tenant to impersonate or access the admin panel.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAdminPanel} variant="outline" size="lg">
                <Shield className="h-4 w-4 mr-2" />
                Admin Panel
              </Button>
              <Button onClick={handleLogout} variant="destructive" size="lg">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-3">Select Tenant to Impersonate</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Choose a tenant to access their workspace for support and troubleshooting purposes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
            {tenants.map((tenant) => (
              <Card
                key={tenant.tenantId}
                className={`cursor-pointer hover:shadow-lg transition-all ${
                  !tenant.isActive ? 'opacity-60' : 'hover:border-blue-500'
                } ${loading ? 'pointer-events-none' : ''}`}
                onClick={() => !loading && tenant.isActive && handleTenantSelect(tenant.tenantId)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <Building2 className={`h-6 w-6 ${tenant.isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg">{tenant.tenantName}</h4>
                      <p className="text-xs text-muted-foreground mt-1">{tenant.tenantId}</p>
                      {!tenant.isActive && (
                        <span className="inline-block mt-2 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {tenants.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No tenants available</p>
            </div>
          )}

          {loading && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <Card className="p-6">
                <div className="flex items-center gap-3">
                  <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                  <span className="text-lg">Accessing tenant...</span>
                </div>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
